import React, { useCallback, useMemo, useState } from "react";
import { useOutletContext, useSearchParams } from "react-router-dom";
import { FiClock, FiRefreshCw, FiAlertTriangle } from "react-icons/fi";
import DataTable from "../common/DataTable";
import TablePagination from "../common/TablePagination";
import StatusBadge from "../common/StatusBadge";
import RoomChip from "../common/RoomChip";
import useAdminResource from "../../hooks/useAdminResource";
import adminUserService from "../../services/adminUserService";
import { ScheduleDetailModal } from "./DetailModals";
import { formatDateTime, dash } from "./scheduleFormat";
import { deviceDisplayName } from "../../utils/deviceName";

/**
 * Upcoming — the user's precomputed calendar (`schedulenextruns`), one row per
 * occurrence.
 *
 * This is a different question from the Schedules tab. Schedules answers "what
 * rules exist"; this answers "what will actually fire, when, on which device" —
 * which is the question you have when a user says a light switched at the wrong
 * time.
 *
 * ⚠️ Rows can be in the PAST. The collection is TTL'd on `expireAt`, not on
 * nextRunAt, so an occurrence that already fired (or was missed) lingers until
 * it expires. Showing those silently under a heading called "Upcoming" would be
 * misleading, so they are marked and can be hidden.
 */
const RANGES = [
  ["7", "Next 7 days"],
  ["30", "Next 30 days"],
  ["90", "Next 90 days"],
  ["all", "All"],
];

export default function UserUpcomingTab() {
  const { userId, deviceIndex } = useOutletContext();

  const DEFAULTS = { range: "30", hidePast: "1" };
  const [searchParams, setSearchParams] = useSearchParams();
  const get = (k) => searchParams.get(k) ?? DEFAULTS[k];

  const range = get("range");
  const hidePast = get("hidePast") === "1";
  const page = Number(searchParams.get("page") || 1);
  const limit = Number(searchParams.get("limit") || 20);

  const [detailScheduleId, setDetailScheduleId] = useState(null);

  const patchParams = useCallback(
    (patch) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        for (const [k, v] of Object.entries(patch)) {
          const isDefault =
            v === undefined || v === null || (k in DEFAULTS && String(v) === DEFAULTS[k]);
          if (isDefault) next.delete(k);
          else next.set(k, String(v));
        }
        if (!("page" in patch)) next.delete("page");
        return next;
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setSearchParams]
  );

  const fetcher = useCallback(() => {
    const params = { page, limit };
    if (range !== "all") {
      // `from` is deliberately NOT "now": a run a few minutes ago is exactly
      // what you want to see when diagnosing "it fired at the wrong time".
      const from = new Date(Date.now() - 7 * 864e5);
      const to = new Date(Date.now() + Number(range) * 864e5);
      params.from = from.toISOString();
      params.to = to.toISOString();
    }
    return adminUserService.fetchUserNextRuns(userId, params);
  }, [userId, page, limit, range]);

  const { data, meta, state, error, refetch, dismissError } = useAdminResource(
    fetcher,
    [userId, page, limit, range]
  );

  const now = Date.now();
  const rows = useMemo(() => {
    const all = (data || []).map((r) => ({
      ...r,
      isPast: r.nextRunAt ? new Date(r.nextRunAt).getTime() < now : false,
    }));
    return hidePast ? all.filter((r) => !r.isPast) : all;
  }, [data, hidePast, now]);

  const pastCount = useMemo(
    () =>
      (data || []).filter(
        (r) => r.nextRunAt && new Date(r.nextRunAt).getTime() < now
      ).length,
    [data, now]
  );

  const columns = useMemo(
    () => [
      {
        id: "when",
        header: "Fires at",
        cell: ({ row }) => {
          const r = row.original;
          return (
            <div className="min-w-[170px]">
              <span
                className={`text-sm font-semibold ${
                  r.isPast ? "text-slate-400" : "text-slate-800"
                }`}
              >
                {formatDateTime(r.nextRunAt)}
              </span>
              {r.isPast && (
                <span className="mt-1 flex items-center gap-1 text-[11px] text-amber-600">
                  <FiAlertTriangle size={10} /> already passed
                </span>
              )}
            </div>
          );
        },
      },
      {
        id: "schedule",
        header: "Schedule",
        cell: ({ row }) => {
          const r = row.original;
          return (
            <div className="min-w-[150px]">
              <div className="flex items-center gap-1.5">
                <span className="font-semibold text-slate-800">
                  {dash(r.scheduleName)}
                </span>
                {r.smartRestore && (
                  <span title="Smart Restore is on" className="text-indigo-500">✨</span>
                )}
              </div>
              <p className="mt-0.5 text-[11px] capitalize text-slate-400">
                {dash(r.scheduleType)}
              </p>
            </div>
          );
        },
      },
      {
        id: "status",
        header: "Status",
        cell: ({ row }) => (
          <div className="space-y-1">
            <StatusBadge kind="scheduleStatus" value={row.original.scheduleStatus} />
            <div>
              <StatusBadge kind="cronStatus" value={row.original.scheduleCronStatus} />
            </div>
          </div>
        ),
      },
      {
        id: "devices",
        header: "Will do",
        cell: ({ row }) => {
          const devices = row.original.devices || [];
          if (!devices.length) {
            return <span className="text-sm text-slate-400">No devices</span>;
          }
          return (
            <div className="min-w-[210px] space-y-1">
              {devices.slice(0, 3).map((d, i) => {
                // The calendar stores the vendor id as `id`. The row's own
                // `deviceName` is the top-level alias only, which is null for
                // every Tapo device the user never renamed — so resolve the
                // name from the device index, and treat the device as gone
                // only when the index does not have it.
                const resolved = deviceIndex?.byVendorId?.[d.id];
                const name = resolved
                  ? deviceDisplayName(resolved)
                  : d.deviceName || null;
                return (
                  <div key={`${d.id}-${i}`} className="flex flex-wrap items-center gap-1.5">
                    <StatusBadge kind="powerState" value={d.expectedState} unknownLabel="—" />
                    {name ? (
                      <span className="text-sm text-slate-700">{name}</span>
                    ) : (
                      <span
                        title="This device is no longer in the user's account"
                        className="font-mono text-[11px] text-slate-400"
                      >
                        {d.id}
                      </span>
                    )}
                    {/* Prefer the device's populated room — it carries the
                        user's own colours. The calendar row only stores a room
                        NAME, so when the device is gone fall back to a neutral
                        chip rather than inventing an _id (RoomChip would read a
                        null _id as "Ungrouped" and drop the name entirely). */}
                    {resolved?.roomId ? (
                      <RoomChip room={resolved.roomId} />
                    ) : d.roomName ? (
                      <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-500">
                        {d.roomName}
                      </span>
                    ) : null}
                    {d.brightnessLevel != null && (
                      <span className="text-[11px] text-slate-400">{d.brightnessLevel}%</span>
                    )}
                  </div>
                );
              })}
              {devices.length > 3 && (
                <p className="text-[11px] text-slate-400">
                  +{devices.length - 3} more device{devices.length - 3 === 1 ? "" : "s"}
                </p>
              )}
            </div>
          );
        },
      },
      {
        id: "expireAt",
        header: "Row expires",
        // Useful when a calendar looks wrong: it says when this precomputed row
        // will be dropped by the TTL index.
        cell: ({ row }) => (
          <span className="text-xs text-slate-400">
            {formatDateTime(row.original.expireAt)}
          </span>
        ),
      },
    ],
    [deviceIndex]
  );

  const isFiltered = Object.keys(DEFAULTS).some(
    (k) => (searchParams.get(k) ?? DEFAULTS[k]) !== DEFAULTS[k]
  );

  const select =
    "rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none";

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <select value={range} onChange={(e) => patchParams({ range: e.target.value })} className={select}>
          {RANGES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>

        <label className="flex items-center gap-2 text-sm text-slate-600">
          <input
            type="checkbox"
            checked={hidePast}
            onChange={(e) => patchParams({ hidePast: e.target.checked ? "1" : "0" })}
            className="rounded border-slate-300"
          />
          Hide runs that already passed
          {pastCount > 0 && <span className="text-slate-400">({pastCount})</span>}
        </label>

        <button
          type="button"
          onClick={() => refetch()}
          className="ml-auto inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
        >
          <FiRefreshCw size={14} className={state === "loading" ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>

      <DataTable
        columns={columns}
        data={rows}
        state={state}
        error={error}
        onRetry={() => refetch()}
        onDismissError={dismissError}
        getRowId={(row) => String(row._id)}
        onRowClick={(row) => row.scheduleId && setDetailScheduleId(String(row.scheduleId))}
        emptyIcon={FiClock}
        emptyTitle={isFiltered ? "Nothing in this range" : "No upcoming runs"}
        emptyMessage={
          isFiltered
            ? "Widen the date range, or show runs that already passed."
            : "This user has no precomputed occurrences. A schedule with no next run will not appear here."
        }
        isFiltered={isFiltered}
        onClearFilters={() => setSearchParams(new URLSearchParams())}
      />

      <TablePagination
        page={meta.page}
        totalPages={meta.totalPages}
        total={meta.total}
        limit={meta.limit}
        onPageChange={(p) => patchParams({ page: p })}
        onLimitChange={(l) => patchParams({ limit: l, page: 1 })}
      />

      <ScheduleDetailModal
        open={Boolean(detailScheduleId)}
        onClose={() => setDetailScheduleId(null)}
        userId={userId}
        scheduleId={detailScheduleId}
        deviceIndex={deviceIndex}
      />
    </div>
  );
}
